import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

interface ApiFnKey {
  apiName: string;
  endpointName: string;
}

export interface ErrorFilter extends ApiFnKey {
  status?: FetchBaseQueryError["status"][];
}

export interface ErrorInstance extends ApiFnKey {
  status: FetchBaseQueryError["status"];
}

export const errorSuppressionSliceName = "errorSuppression";

export interface ErrorSuppressionState {
  suppressedErrors: Partial<Record<string, ErrorFilter[]>>;
}

const initialState: ErrorSuppressionState = {
  suppressedErrors: {},
};

const errorSuppressionSlice = createSlice({
  name: errorSuppressionSliceName,
  initialState,
  reducers: {
    suppressErrors(state: ErrorSuppressionState, action: PayloadAction<ErrorFilter>) {
      const lookupKey = createLookupKey(action.payload);
      const filters = state.suppressedErrors[lookupKey] ?? [];
      addErrorFilterIfNotPresent(filters, action.payload);
      state.suppressedErrors[lookupKey] = filters;
    },
    stopSuppressingErrors(state: ErrorSuppressionState, action: PayloadAction<ErrorFilter>) {
      const lookupKey = createLookupKey(action.payload);
      const filters = state.suppressedErrors[lookupKey] ?? [];
      removeErrorFilterIfPresent(filters, action.payload);
      state.suppressedErrors[lookupKey] = filters;
    },
  },
});

export interface ErrorSuppressionRootState {
  [errorSuppressionSliceName]: ErrorSuppressionState;
}

export const selectErrorSuppression = (state: ErrorSuppressionRootState) => state[errorSuppressionSliceName];
export const selectIsErrorInstanceSuppressed = (error: ErrorInstance) => (state: ErrorSuppressionRootState) => {
  const { suppressedErrors } = selectErrorSuppression(state);
  const filters = suppressedErrors[createLookupKey(error)];
  if (filters == null || filters.length === 0) {
    return false;
  }

  return filters.some((it) => doesFilterMatch(it, error));
};

export const { suppressErrors, stopSuppressingErrors } = errorSuppressionSlice.actions;
export const errorSuppressionReducer = errorSuppressionSlice.reducer;

// utility functions
const doesFilterMatch = (filter: ErrorFilter, error: ErrorInstance): boolean => {
  return filter.status == null || filter.status.includes(error.status);
};

const addErrorFilterIfNotPresent = (filters: ErrorFilter[], filter: ErrorFilter) => {
  const filterAsString = JSON.stringify(filter);
  if (!filters.find((it) => JSON.stringify(it) === filterAsString)) {
    filters.push(filter);
  }
};

const removeErrorFilterIfPresent = (filters: ErrorFilter[], filter: ErrorFilter) => {
  const filterAsString = JSON.stringify(filter);
  const filterIndex = filters.findIndex((it) => JSON.stringify(it) === filterAsString);
  if (filterIndex >= 0) filters.splice(filterIndex, 1);
};

const createLookupKey = (error: ApiFnKey): string => {
  return `${error.apiName}/${error.endpointName}`;
};
